import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getSegment } from "@/apis/bacchus/segment";
import { getSegmentIdFromSearch } from "@/utils/companyRouteContext";

const MAX_SEGMENT_NAME_CACHE_SIZE = 200;
const segmentNameCache = new Map<string, string>();
const inFlightSegmentRequests = new Map<string, Promise<string | undefined>>();

const getCachedSegmentName = (segmentId: string): string | undefined => {
  const name = segmentNameCache.get(segmentId);
  if (!name) return undefined;

  segmentNameCache.delete(segmentId);
  segmentNameCache.set(segmentId, name);
  return name;
};

const setCachedSegmentName = (segmentId: string, name: string): void => {
  if (segmentNameCache.has(segmentId)) {
    segmentNameCache.delete(segmentId);
  }
  segmentNameCache.set(segmentId, name);

  if (segmentNameCache.size <= MAX_SEGMENT_NAME_CACHE_SIZE) return;
  const oldestKey = segmentNameCache.keys().next().value;
  if (oldestKey) {
    segmentNameCache.delete(oldestKey);
  }
};

const fetchSegmentName = (segmentId: string): Promise<string | undefined> => {
  const cachedName = getCachedSegmentName(segmentId);
  if (cachedName) {
    return Promise.resolve(cachedName);
  }

  const inFlightRequest = inFlightSegmentRequests.get(segmentId);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const request = getSegment(segmentId)
    .then((segment) => {
      const name = segment?.name ?? undefined;
      if (name) {
        setCachedSegmentName(segmentId, name);
      }
      return name;
    })
    .catch(() => undefined)
    .finally(() => {
      inFlightSegmentRequests.delete(segmentId);
    });

  inFlightSegmentRequests.set(segmentId, request);
  return request;
};

export interface CompanySegmentContext {
  segmentId?: string;
  segmentName?: string;
  isLoadingSegment: boolean;
}

export const useCompanySegmentContext = (): CompanySegmentContext => {
  const [searchParams] = useSearchParams();
  const segmentId = getSegmentIdFromSearch(searchParams);
  const [segmentName, setSegmentName] = useState<string | undefined>(undefined);
  const [isLoadingSegment, setIsLoadingSegment] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    if (!segmentId) {
      setSegmentName(undefined);
      setIsLoadingSegment(false);
      return;
    }

    const cachedName = getCachedSegmentName(segmentId);
    if (cachedName) {
      setSegmentName(cachedName);
      setIsLoadingSegment(false);
      return;
    }

    setSegmentName(undefined);
    setIsLoadingSegment(true);

    fetchSegmentName(segmentId)
      .then((name) => {
        if (isCancelled) return;
        setSegmentName(name);
      })
      .finally(() => {
        if (isCancelled) return;
        setIsLoadingSegment(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [segmentId]);

  return {
    segmentId,
    segmentName,
    isLoadingSegment,
  };
};
